import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from '../../../services/axiosInstance';
import { DashboardAreaIcon } from './DashboardAreaIcon';
import { AREAS, AREA_KEYS, AreaKey } from './adminModules';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAdminSocket } from '../../../hooks/useAdminSocket';
import { avatarSrc } from '../../../utils/photoUrl';
import { ArrowDown, ArrowUpRight, ChevronDown, Menu, Search, X } from 'lucide-react';
import '../../../styles/admin-v2.css';
import '../../../styles/admin-dashboard.css';

/* ============================================================
   Spazio di lavoro dell'area amministrativa V2: sidebar con le aree e
   i moduli (compressa a ogni nuova sessione), barra comandi con ricerca
   moduli/associati (Ctrl+K) e azioni della pagina. Usato dalla Dashboard
   (variante "home") e da V2Shell, cioè da ogni pagina admin in modalità V2.
   ============================================================ */

/** Altezza della barra comandi sticky: le barre sticky delle pagine si allineano sotto. */
export const ADMIN_BAR_HEIGHT = 67;
const DESKTOP_QUERY = '(min-width: 901px)';
/** Sopra i 900px la sidebar può essere compressa; sotto diventa il menu a scomparsa. */
export const useIsDesktop = (): boolean => {
  const [desktop, setDesktop] = useState(() => { try { return !!window.matchMedia?.(DESKTOP_QUERY)?.matches; } catch { return false; } });
  useEffect(() => {
    let mq: MediaQueryList | undefined;
    try { mq = window.matchMedia?.(DESKTOP_QUERY); } catch { return; }
    if (!mq || typeof mq.addEventListener !== 'function') return;
    const onChange = () => setDesktop(!!mq?.matches);
    mq.addEventListener('change', onChange);
    return () => mq?.removeEventListener('change', onChange);
  }, []);
  return desktop;
};
export const SIDEBAR_SESSION_KEY = 'unasca.admin.sidebar';

/** All'accesso (nuova sessione del browser) la sidebar parte compressa. */
export const readSidebarCollapsed = (): boolean => {
  try { return sessionStorage.getItem(SIDEBAR_SESSION_KEY) !== 'expanded'; } catch { return true; }
};
export const writeSidebarCollapsed = (collapsed: boolean): void => {
  try { sessionStorage.setItem(SIDEBAR_SESSION_KEY, collapsed ? 'collapsed' : 'expanded'); } catch { /* Sessione corrente. */ }
};

/** La voce è attiva sull'indirizzo esatto o su un suo sotto-percorso (la home solo su /admin). */
export const isActiveHref = (pathname: string, href: string): boolean => {
  const target = href.split(/[?#]/)[0].replace(/\/+$/, '');
  const current = pathname.replace(/\/+$/, '') || '/';
  if (target === '/admin') return current === '/admin';
  return current === target || current.startsWith(target + '/');
};

export interface WorkspaceModule { key: string; name: string; sub: string; href: string; area: string; pend: number }

/* —— Ricerca rapida: rappresentanti e cariche associative —— */

export interface RepresentativeHit {
  _id: string;
  nome?: string;
  cognome?: string;
  status?: string;
  associateId?: { nominativo?: string } | null;
  cariche?: Array<{ caricaId?: { nome?: string } | null; dataFine?: string | null }>;
}
export interface CaricaHit { _id: string; nome: string; descrizione?: string; categoria?: string; settore?: string; activeRepresentatives?: number }

export const REPRESENTATIVES_MIN_CHARS = 3;
export const CARICHE_MIN_CHARS = 2;

const fold = (value: string): string => value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLocaleLowerCase();

export const representativeName = (r: RepresentativeHit): string => [r.nome, r.cognome].filter(Boolean).join(' ') || 'Rappresentante';

/** Cariche in corso, impresa collegata e stato se non attivo. */
export const representativeSubtitle = (r: RepresentativeHit): string => {
  const cariche = (r.cariche || []).filter(c => !c.dataFine && c.caricaId?.nome).map(c => c.caricaId!.nome as string);
  const parts = [cariche.join(', ') || 'Nessuna carica in corso', r.associateId?.nominativo];
  if (r.status && r.status !== 'attivo') parts.push(r.status);
  return parts.filter(Boolean).join(' · ');
};

/** Cariche che contengono tutte le parole cercate (nome, descrizione, categoria, settore; accenti ignorati). */
export const matchCariche = (list: CaricaHit[], rawTerm: string, max = 5): CaricaHit[] => {
  const words = fold(rawTerm.trim()).split(/\s+/).filter(Boolean);
  if (!words.length || rawTerm.trim().length < CARICHE_MIN_CHARS) return [];
  return list
    .filter(c => {
      const text = fold([c.nome, c.descrizione, c.categoria, c.settore].filter(Boolean).join(' '));
      return words.every(w => text.includes(w));
    })
    .slice(0, max);
};

const capitalize = (value?: string): string => (value ? value.charAt(0).toUpperCase() + value.slice(1) : '');

/** Segnaposto della ricerca: elenca solo ciò che l'operatore può cercare. */
export const searchPlaceholder = (associates: boolean, representatives: boolean, cariche: boolean): string => {
  const what = ['moduli', associates && 'associati', representatives && 'rappresentanti', cariche && 'cariche'].filter(Boolean) as string[];
  if (what.length === 1) return 'Cerca un modulo…';
  return `Cerca ${what.slice(0, -1).join(', ')} o ${what[what.length - 1]}…`;
};
export interface WorkspaceArea { key: string; label: string; icon?: React.ReactNode }

const SidebarAccount: React.FC = () => {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const name = user?.profile?.fullName || user?.username || 'Amministratore';
  const photo = avatarSrc(user || undefined);
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => { setImageFailed(false); }, [photo]);
  const initials = name.trim().split(/\s+/).slice(0, 2).map(part => part[0]).join('').toUpperCase();
  return (
    <div className="dash-sidebar-account">
      <Link to="/profile" className="dash-account-profile" aria-label={`Profilo di ${name}`} title={name}>
        <span className="dash-avatar-bezel">{photo && !imageFailed ? <img src={photo} alt="" onError={() => setImageFailed(true)} /> : <span className="dash-account-initials">{initials}</span>}</span>
        <span className="dash-account-name">{name}<small>Il mio profilo</small></span>
      </Link>
      <button className="dash-sidebar-theme" onClick={toggleTheme} aria-label={theme === 'dark' ? 'Attiva tema chiaro dalla sidebar' : 'Attiva tema scuro dalla sidebar'} title={theme === 'dark' ? 'Tema chiaro' : 'Tema scuro'}>
        <img className="dash-control-art" src={`/dashboard-icons/${theme === 'dark' ? 'sun' : 'moon'}-3d.png`} alt="" aria-hidden="true" width={28} height={28} />
      </button>
    </div>
  );
};

export interface AdminWorkspaceProps {
  children: React.ReactNode;
  modules: WorkspaceModule[];
  /** Aree mostrate nella sidebar (default: tutte quelle con almeno un modulo visibile) */
  areas?: WorkspaceArea[];
  /** Ricerca controllata dalla pagina (Dashboard); altrimenti stato interno */
  query?: string;
  onQuery?: (query: string) => void;
  toolbarActions?: React.ReactNode;
  /** Invio nella ricerca: la Dashboard apre la griglia dei moduli; altrove si apre il primo modulo trovato */
  onShowModules?: () => void;
  /** Briciole nella barra comandi (default: "Workspace / Panoramica") */
  breadcrumb?: React.ReactNode;
  /** Variante Dashboard: attiva gli stili della home (.dash-home) */
  home?: boolean;
}

export const AdminWorkspace: React.FC<AdminWorkspaceProps> = ({ children, modules, areas, query: queryProp, onQuery: onQueryProp, toolbarActions, onShowModules, breadcrumb, home }) => {
  const location = useLocation();
  const navigate = useNavigate();
  // Presenza operatore su ogni pagina V2 (conteggio "Collegati ora" nella dashboard)
  useAdminSocket();
  const [queryState, setQueryState] = useState('');
  const query = queryProp ?? queryState;
  const onQuery = onQueryProp ?? setQueryState;
  const areaList: WorkspaceArea[] = areas ?? AREA_KEYS.map((key: AreaKey) => ({ key, label: AREAS[key].label }));

  const [collapsed, setCollapsed] = useState(readSidebarCollapsed);
  const changeCollapsed = (value: boolean) => { setCollapsed(value); writeSidebarCollapsed(value); };
  const [mobileOpen, setMobileOpen] = useState(false);
  const desktop = useIsDesktop();
  // Sidebar compressa: il pannello dell'area si apre accanto (sopra il contenuto, senza spostarlo)
  const [flyout, setFlyout] = useState<{ key: string; top: number } | null>(null);
  const flyMode = collapsed && desktop;
  // Chiusura ritardata: spostando il mouse dall'icona al pannello (attraverso il bordo della
  // sidebar) il pannello resta aperto; si chiude solo se il mouse è davvero uscito.
  const closeTimer = useRef<number | undefined>(undefined);
  const cancelClose = () => { if (closeTimer.current) { window.clearTimeout(closeTimer.current); closeTimer.current = undefined; } };
  const closeFlyoutSoon = () => { cancelClose(); closeTimer.current = window.setTimeout(() => setFlyout(null), 220); };
  const openFlyout = (key: string, el: HTMLElement) => {
    cancelClose();
    const top = el.getBoundingClientRect().top;
    setFlyout({ key, top: Math.max(8, Math.min(top, window.innerHeight - 340)) });
  };
  useEffect(() => () => cancelClose(), []);
  useEffect(() => { setMobileOpen(false); setFlyout(null); }, [location.pathname]);
  useEffect(() => { if (!flyMode) setFlyout(null); }, [flyMode]);

  const input = useRef<HTMLInputElement>(null);
  const canSearchAssociates = modules.some(m => m.key === 'associates');
  const [searchOpen, setSearchOpen] = useState(false);
  const [associates, setAssociates] = useState<Array<{ _id: string; nominativo: string; codice: string; partitaIVA?: string; email?: string }>>([]);
  const [searchStatus, setSearchStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  useEffect(() => {
    const term = query.trim();
    setAssociates([]);
    if (!canSearchAssociates || !searchOpen || term.length < 5) { setSearchStatus('idle'); return; }
    const controller = new AbortController();
    setSearchStatus('loading');
    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ search: term, page: '1', limit: '6' });
        const { data } = await axios.get(`/admin/associates?${params}`, { signal: controller.signal });
        if (!controller.signal.aborted) { setAssociates(data.data || []); setSearchStatus('done'); }
      } catch {
        if (!controller.signal.aborted) setSearchStatus('error');
      }
    }, 350);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [query, canSearchAssociates, searchOpen]);

  // Rappresentanti associativi: stessa ricerca della pagina Rappresentanti (nome, cognome, email)
  const canSearchRepresentatives = modules.some(m => m.key === 'representatives');
  const [representatives, setRepresentatives] = useState<RepresentativeHit[]>([]);
  const [repsStatus, setRepsStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  useEffect(() => {
    const term = query.trim();
    setRepresentatives([]);
    if (!canSearchRepresentatives || !searchOpen || term.length < REPRESENTATIVES_MIN_CHARS) { setRepsStatus('idle'); return; }
    const controller = new AbortController();
    setRepsStatus('loading');
    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ search: term, page: '1', limit: '6' });
        const { data } = await axios.get(`/admin/representatives?${params}`, { signal: controller.signal });
        if (!controller.signal.aborted) { setRepresentatives(data.data || []); setRepsStatus('done'); }
      } catch {
        if (!controller.signal.aborted) setRepsStatus('error');
      }
    }, 350);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [query, canSearchRepresentatives, searchOpen]);

  // Cariche associative: poche decine, caricate una volta all'apertura della ricerca e filtrate qui
  const canSearchCariche = modules.some(m => m.key === 'cariche');
  const [cariche, setCariche] = useState<CaricaHit[]>([]);
  const [caricheStatus, setCaricheStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const caricheRequested = useRef(false);
  useEffect(() => {
    if (!canSearchCariche || !searchOpen || caricheRequested.current) return;
    caricheRequested.current = true;
    setCaricheStatus('loading');
    axios.get('/admin/associative-roles')
      .then(({ data }) => { setCariche(data.data || []); setCaricheStatus('done'); })
      .catch(() => { caricheRequested.current = false; setCaricheStatus('error'); });
  }, [canSearchCariche, searchOpen]);
  const caricheMatches = matchCariche(cariche, query);

  const menu = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault(); input.current?.focus();
      }
      if (event.key === 'Escape') {
        setSearchOpen(false);
        setMobileOpen(false);
        if (mobileOpen) menu.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileOpen]);

  const term = query.trim().toLocaleLowerCase();
  const moduleMatches = modules.filter(m => `${m.name} ${m.sub}`.toLocaleLowerCase().includes(term));
  const activeArea = areaList.find(a => modules.some(m => m.area === a.key && isActiveHref(location.pathname, m.href)))?.key;
  const submitSearch = () => {
    setSearchOpen(false);
    if (onShowModules) onShowModules();
    else if (term && moduleMatches[0]) navigate(moduleMatches[0].href);
  };

  return (
    <div className={`adm2 dash-modern${home ? ' dash-home' : ''}${collapsed ? ' dash-collapsed' : ''}${mobileOpen ? ' dash-menu-open' : ''}`}>
      <a className="dash-skip" href="#dashboard-content">Vai al contenuto</a>
      <aside className="dash-sidebar" id="dashboard-navigation" aria-label="Navigazione amministrazione">
        <Link to="/admin" className="dash-brand" aria-label="UNASCA, dashboard">
          <picture className="dash-brand-crop"><img src="/dashboard-icons/unasca-logo-3d.png" alt="UNASCA" /></picture>
          <img className="dash-brand-symbol-3d" src="/dashboard-icons/unasca-symbol-3d.png" alt="UNASCA" width={44} height={44} />
        </Link>
        <div className="dash-sidebar-heading">Il tuo spazio di lavoro</div>
        <Link to="/admin" className="dash-nav-home" aria-label="Panoramica" title="Panoramica" aria-current={location.pathname === '/admin' ? 'page' : undefined}><img className="dash-area-art dash-home-art" src="/dashboard-icons/overview.png" alt="" aria-hidden="true" width={36} height={36} /><span className="dash-nav-label">Panoramica</span><i /></Link>
        <nav aria-label="Moduli amministrativi">
          <div className="dash-sidebar-heading">Aree di gestione</div>
          {areaList.filter(a => modules.some(m => m.area === a.key)).map(a => {
            const areaModules = modules.filter(m => m.area === a.key);
            const links = areaModules.map(m => (
              <Link key={m.key} to={m.href} aria-current={isActiveHref(location.pathname, m.href) ? 'page' : undefined}>{m.name}{m.pend > 0 && <span>{m.pend}</span>}</Link>
            ));
            if (flyMode) {
              return (
                <div className="dash-nav-area dash-nav-fly" key={a.key}
                  onMouseEnter={e => openFlyout(a.key, e.currentTarget)} onMouseLeave={closeFlyoutSoon}
                  onFocus={e => openFlyout(a.key, e.currentTarget)} onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setFlyout(null); }}>
                  <Link to={areaModules[0].href} className="dash-nav-icon" aria-label={a.label} title={a.label} aria-haspopup="true" aria-expanded={flyout?.key === a.key} aria-current={activeArea === a.key ? 'true' : undefined}>
                    <DashboardAreaIcon area={a.key} />
                  </Link>
                  {flyout?.key === a.key && (
                    <div className="dash-flyout" role="group" aria-label={a.label} style={{ top: flyout.top }}>
                      <div className="dash-flyout-title">{a.label}</div>
                      {links}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <details className="dash-nav-area" key={`${a.key}:${activeArea === a.key}`} open={activeArea === a.key || undefined}>
                <summary aria-label={a.label} title={a.label} onClick={() => { if (collapsed) changeCollapsed(false); }}><DashboardAreaIcon area={a.key} /><span>{a.label}</span><ChevronDown size={15} /></summary>
                <div>{links}</div>
              </details>
            );
          })}
        </nav>
        <button className="dash-sidebar-collapse" onClick={() => changeCollapsed(!collapsed)} aria-label={collapsed ? 'Espandi sidebar' : 'Comprimi sidebar'} aria-expanded={!collapsed}><img className={`dash-control-art${collapsed ? ' dash-control-art--expand' : ''}`} src="/dashboard-icons/sidebar-toggle-3d.png" alt="" aria-hidden="true" width={28} height={28} /><span>Comprimi menu</span></button>
        <SidebarAccount />
      </aside>
      <div className="dash-main">
        <div className="dash-commandbar">
          <button className="dash-icon-button dash-desktop-toggle" aria-label={collapsed ? 'Espandi navigazione' : 'Comprimi navigazione'} aria-expanded={!collapsed} aria-controls="dashboard-navigation" onClick={() => changeCollapsed(!collapsed)}><img className={`dash-control-art${collapsed ? ' dash-control-art--expand' : ''}`} src="/dashboard-icons/sidebar-toggle-3d.png" alt="" aria-hidden="true" width={28} height={28} /></button>
          <button ref={menu} className="dash-icon-button dash-mobile-toggle" aria-label={mobileOpen ? 'Chiudi menu' : 'Apri menu'} aria-expanded={mobileOpen} aria-controls="dashboard-navigation" onClick={() => setMobileOpen(!mobileOpen)}>{mobileOpen ? <X size={20} /> : <Menu size={20} />}</button>
          {breadcrumb ?? <span className="dash-breadcrumb">Workspace <span>/</span> <strong>Panoramica</strong></span>}
          <form className="dash-search" role="search" onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setSearchOpen(false); }} onKeyDown={e => { if (e.key === 'Escape') { setSearchOpen(false); input.current?.focus(); } }} onSubmit={e => { e.preventDefault(); submitSearch(); }}>
            <Search size={18} />
            <input ref={input} aria-label="Cerca nell'amministrazione" placeholder={searchPlaceholder(canSearchAssociates, canSearchRepresentatives, canSearchCariche)} value={query} onFocus={() => setSearchOpen(true)} onChange={e => { onQuery(e.target.value); setSearchOpen(true); }} aria-controls="dashboard-search-results" />
            {query ? <button type="button" aria-label="Cancella ricerca" onClick={() => { onQuery(''); input.current?.focus(); }}><X size={16} /></button> : <kbd>Ctrl K</kbd>}
            <button type="submit" aria-label="Vai ai risultati" onClick={() => setSearchOpen(false)}><ArrowDown size={16} /></button>
            {searchOpen && query.trim() && <div id="dashboard-search-results" className="dash-search-results">
              <div className="dash-eyebrow">Moduli</div>
              {moduleMatches.slice(0, 5).map(m => <Link key={m.key} to={m.href} onClick={() => setSearchOpen(false)}><span><strong>{m.name}</strong><small>{m.sub}</small></span><ArrowUpRight size={15} /></Link>)}
              {moduleMatches.length === 0 && <p>Nessun modulo trovato.</p>}
              {canSearchAssociates && <>
                <div className="dash-eyebrow">Associati <span>Nome, codice, P.IVA o email</span></div>
                <p role="status">{query.trim().length < 5 ? 'Scrivi almeno 5 caratteri.' : searchStatus === 'loading' ? 'Ricerca in corso…' : searchStatus === 'error' ? 'Ricerca non disponibile. Riprova tra poco.' : searchStatus === 'done' && !associates.length ? 'Nessun associato trovato.' : ''}</p>
                {associates.map(a => <Link key={a._id} to={`/admin/associates/${a._id}`} onClick={() => setSearchOpen(false)}><span><strong>{a.nominativo}</strong><small>{[a.codice, a.partitaIVA, a.email].filter(Boolean).join(' · ')}</small></span><ArrowUpRight size={15} /></Link>)}
                {associates.length === 6 && <p>Primi 6 risultati: aggiungi altri caratteri per affinare.</p>}
              </>}
              {canSearchRepresentatives && <>
                <div className="dash-eyebrow">Rappresentanti associativi <span>Nome, cognome o email</span></div>
                <p role="status">{query.trim().length < REPRESENTATIVES_MIN_CHARS ? `Scrivi almeno ${REPRESENTATIVES_MIN_CHARS} caratteri.` : repsStatus === 'loading' ? 'Ricerca in corso…' : repsStatus === 'error' ? 'Ricerca non disponibile. Riprova tra poco.' : repsStatus === 'done' && !representatives.length ? 'Nessun rappresentante trovato.' : ''}</p>
                {representatives.map(r => (
                  <Link key={r._id} to={`/admin/rappresentanti?search=${encodeURIComponent(representativeName(r))}`} onClick={() => setSearchOpen(false)}>
                    <span><strong>{representativeName(r)}</strong><small>{representativeSubtitle(r)}</small></span><ArrowUpRight size={15} />
                  </Link>
                ))}
                {representatives.length === 6 && <p>Primi 6 risultati: aggiungi altri caratteri per affinare.</p>}
              </>}
              {canSearchCariche && <>
                <div className="dash-eyebrow">Cariche associative <span>Si apre l'elenco dei titolari</span></div>
                <p role="status">{query.trim().length < CARICHE_MIN_CHARS ? `Scrivi almeno ${CARICHE_MIN_CHARS} caratteri.` : caricheStatus === 'loading' ? 'Caricamento cariche…' : caricheStatus === 'error' ? 'Cariche non disponibili. Riprova tra poco.' : caricheStatus === 'done' && !caricheMatches.length ? 'Nessuna carica trovata.' : ''}</p>
                {caricheMatches.map(c => (
                  <Link key={c._id} to={`/admin/cariche-associative?carica=${encodeURIComponent(c._id)}`} onClick={() => setSearchOpen(false)}>
                    <span>
                      <strong>{c.nome}</strong>
                      <small>{[capitalize(c.categoria), capitalize(c.settore), `${c.activeRepresentatives ?? 0} titolar${c.activeRepresentatives === 1 ? 'e' : 'i'}`].filter(Boolean).join(' · ')}</small>
                    </span><ArrowUpRight size={15} />
                  </Link>
                ))}
              </>}
            </div>}
          </form>
          {toolbarActions}
        </div>
        <div id="dashboard-content" tabIndex={-1}>{children}</div>
      </div>
    </div>
  );
};

export default AdminWorkspace;
